import { forwardRef } from 'react';
import Chip, { type ChipProps } from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import type { Priority } from '../../types/ticket';

export interface AppChipProps extends Omit<ChipProps, 'color'> {
  /**
   * Chip variant for different use cases
   */
  chipVariant?: 'priority' | 'status' | 'category' | 'default';
  /**
   * Priority level (required when chipVariant is 'priority')
   */
  priority?: Priority;
  /**
   * Whether the status is active (for status variant)
   */
  active?: boolean;
}

/**
 * AppChip - Badge/tag component
 *
 * Wraps MUI Chip with our design system patterns.
 * Use for priority badges, status indicators, and category tags.
 */
export const AppChip = forwardRef<HTMLDivElement, AppChipProps>(
  ({ chipVariant = 'default', priority, active, sx, ...props }, ref) => {
    const theme = useTheme();
    const priorityColors = theme.palette.priority;

    const getStyles = () => {
      switch (chipVariant) {
        case 'priority':
          if (!priority) return {};
          const pKey = priority.toLowerCase() as 'p1' | 'p2' | 'p3' | 'p4';
          return {
            backgroundColor: priorityColors[pKey].bg,
            color: priorityColors[pKey].main,
            border: `1px solid ${priorityColors[pKey].main}30`,
            fontWeight: 500,
          };
        case 'status':
          if (active) {
            return {
              backgroundColor: theme.palette.success.main + '15',
              color: theme.palette.success.dark,
              border: `1px solid ${theme.palette.success.main}50`,
            };
          }
          return {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
          };
        case 'category':
          return {
            backgroundColor: theme.palette.info.main + '15',
            color: theme.palette.info.dark,
            border: `1px solid ${theme.palette.info.main}30`,
          };
        default:
          return {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.secondary,
          };
      }
    };

    return (
      <Chip
        ref={ref}
        size="small"
        sx={{
          ...getStyles(),
          ...sx,
        }}
        {...props}
      />
    );
  }
);

AppChip.displayName = 'AppChip';
